// T-273 — QzPrintAdapter: stampa un'etichetta tramite QZ Tray su una stampante
// di sistema scelta PER NOME (la predefinita di Windows resta libera per
// Word/Excel). Renderizza col rasterizzatore reale (renderLabelCanvas, 203 DPI,
// barcode/QR veri → parità con la stampa Bluetooth), poi invia l'immagine a QZ.
// Solo web/PWA. Il nome della stampante è salvato in `printers.address`.

import { Capacitor } from '@capacitor/core';
import type {
  PrinterAdapter,
  PrinterDevice,
  PrinterSession,
  PrinterStatus,
  LabelTemplate,
} from '@trackeat/shared';
import { renderLabelCanvas } from './labelRaster.js';
import { qzPrintLabel } from './qz-transport.js';

export class QzPrintAdapter implements PrinterAdapter {
  readonly driver = 'qz' as const;

  async discover(): Promise<PrinterDevice[]> {
    // La scoperta delle stampanti QZ avviene nella UI dedicata
    // (PrinterConfigPage → "Cerca stampanti QZ Tray"), che richiede la
    // connessione all'helper locale. Qui niente auto-discovery.
    return [];
  }

  async connect(device: PrinterDevice): Promise<PrinterSession> {
    // Nessuna connessione fisica: la stampa passa dall'helper QZ Tray.
    // deviceId = nome esatto della stampante di sistema.
    return { deviceId: device.address, driver: this.driver, connection: device.connection };
  }

  async disconnect(_session: PrinterSession): Promise<void> {
    // no-op: la websocket QZ resta aperta tra una stampa e l'altra.
  }

  async printLabel(
    session: PrinterSession,
    template: LabelTemplate,
    data: Record<string, unknown>,
    copies: number = 1,
  ): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      throw new Error('QZ Tray è disponibile solo nella versione web (PWA).');
    }
    const canvas = renderLabelCanvas(template, data);
    const png = canvas.toDataURL('image/png');
    await qzPrintLabel({
      printerName: session.deviceId,
      pngDataUrl: png,
      widthMm: template.width_mm,
      heightMm: template.height_mm,
      dpi: 203,
      copies,
    });
  }

  async getStatus(_session: PrinterSession): Promise<PrinterStatus> {
    return {
      online: true,
      paper_ok: true,
      cover_closed: true,
      battery_pct: null,
      error_code: null,
    };
  }
}
